import type { TDesignSystemRoot } from "@/shared/types";
import { buildDesignSystemFontFaces } from "@/shared/utils/custom-font-faces";
import { withDesignSystemDefaults } from "@/shared/utils/design-system-defaults";
import {
  resolveBooleanCssTransforms,
  resolveDesignSystemVariables,
} from "@/shared/utils/resolve-design-system-variables";
import type { TOrgInfo } from "@/types";
import type { StoryType } from "@config";

export const handleDesignSystem = (story: StoryType, orgInfo: TOrgInfo) => {
  // Backfill display/heading defaults for stories saved before those keys
  // existed — otherwise `--display-*` / `--heading-*` CSS variables would
  // never be emitted into published HTML. Mirrors the editor path in
  // handle-design.ts.
  const design = withDesignSystemDefaults(
    story.settings.designSystemV2 as unknown as Record<string, unknown>,
  ) as unknown as TDesignSystemRoot;

  return `
    <style>
    ${handleGoogleFontsImport(design)}
    ${handleCustomFonts(orgInfo, design)}
    ${handleDesignSystemVariables(design)}
    </style>
`;
};

const SYSTEM_FONTS = new Set([
  "arial",
  "arial+black",
  "arial+narrow",
  "book+antiqua",
  "comic+sans+ms",
  "courier",
  "courier+new",
  "georgia",
  "helvetica",
  "helvetica+neue",
  "impact",
  "lucida+console",
  "lucida+sans+unicode",
  "palatino+linotype",
  "segoe+ui",
  "symbol",
  "tahoma",
  "times+new+roman",
  "trebuchet+ms",
  "verdana",
  "webdings",
  "wingdings",
]);

const FONT_KEYS = [
  "titleFont",
  "subtitleFont",
  "paragraphFont",
  "displayFont",
  "headingFont",
] as const;

const handleGoogleFontsImport = (design: TDesignSystemRoot) => {
  const encoded = FONT_KEYS.map(
    (key) => (design as Record<string, unknown>)[key],
  )
    .filter((f): f is string => typeof f === "string" && f.length > 0)
    .map(encodeFont)
    .filter((f) => !SYSTEM_FONTS.has(f.toLowerCase()));

  const families = Array.from(new Set(encoded)).map(
    (f) => `family=${f}:wght@300;400;600;700;900`,
  );

  if (families.length === 0) return "";

  return `@import url('https://fonts.googleapis.com/css2?${families.join("&")}&family=Inter:wght@400;600');`;
};

const handleCustomFonts = (orgInfo: TOrgInfo, design: TDesignSystemRoot) => {
  // The org record can be missing (see ensureOrgInfo); without it there are
  // no custom fonts to declare, which is a degraded preview rather than none.
  const { custom } = orgInfo?.fonts || {};
  const roleTitles = FONT_KEYS.map(
    (key) => (design as Record<string, unknown>)[key],
  ) as (string | undefined)[];
  // Expands each role family to include its heavier sibling weights so bold
  // text resolves to a real face instead of a synthetic/absent bold.
  return buildDesignSystemFontFaces(custom, roleTitles);
};

const handleDesignSystemVariables = (design: TDesignSystemRoot) => {
  const raw = design as unknown as Record<string, unknown>;
  // Boolean flags (hyperlink bold/italic/underline, media filter) are
  // skipped by resolveDesignSystemVariables and emitted here as CSS values.
  const pairs = [
    ...resolveDesignSystemVariables(raw),
    ...resolveBooleanCssTransforms(raw),
  ];
  // A value that could close the declaration or the <style> block is dropped:
  // design values can come from the AI agent, not only the design panel.
  const body = pairs
    .filter(([, value]) => !/[<>{};]/.test(String(value)))
    .map(([name, value]) => `${name}:${value}`)
    .join(";");
  return `:root{ ${body} }`;
};

const encodeFont = (fontName: string) => {
  // Remove everything from the first comma (and the comma itself) onward
  const baseFont = fontName.split(",")[0].trim();
  return baseFont.replace(/ /g, "+");
};
